/**
 * Utilidades para ajustar texto dentro de una zona editable de un sticker.
 *
 * El ajuste se hace SIEMPRE relativo al tamaño del sticker renderizado
 * (`stickerWidth` / `stickerHeight`). Eso hace que el texto se comporte
 * coherentemente tanto si el sticker se ve a 100px como a 400px.
 */

import { useEffect, useState } from "react";

/**
 * Extrae el primer nombre de familia de un string CSS `font-family`.
 * Ejemplo: "'First Love', cursive" -> "First Love".
 */
export const parseFontFamilyName = (fontFamily) => {
	if (!fontFamily) return null;
	const first = String(fontFamily).split(",")[0].trim();
	return first.replace(/^["']|["']$/g, "");
};

/**
 * Hook que devuelve `true` cuando las fuentes indicadas están listas para
 * pintarse. Fuerza un re-render al resolver, evitando que Konva/canvas pinten
 * con la fuente de fallback antes de que la TTF real esté cargada.
 */
export const useStickerFontsReady = (fontFamilies = []) => {
	const [ready, setReady] = useState(false);

	useEffect(() => {
		if (typeof document === "undefined" || !document.fonts) {
			setReady(true);
			return;
		}
		const names = (Array.isArray(fontFamilies) ? fontFamilies : [fontFamilies])
			.map(parseFontFamilyName)
			.filter(Boolean);
		if (names.length === 0) {
			setReady(true);
			return;
		}
		let cancelled = false;
		const specs = names.map((n) => `16px "${n}"`);
		Promise.all(specs.map((s) => document.fonts.load(s).catch(() => null)))
			.then(() => {
				if (!cancelled) setReady(true);
			});
		return () => {
			cancelled = true;
		};
	}, [JSON.stringify(fontFamilies)]);

	return ready;
};

let _measureCtx = null;
const getMeasureCtx = () => {
	if (_measureCtx) return _measureCtx;
	if (typeof document === "undefined") return null;
	const c = document.createElement("canvas");
	_measureCtx = c.getContext("2d");
	return _measureCtx;
};

const buildCanvasFont = (fontSize, textConfig) => {
	const weight = textConfig.fontWeight || 400;
	const family = textConfig.fontFamily || "sans-serif";
	const style = textConfig.fontStyle === "italic" ? "italic " : "";
	return `${style}${weight} ${fontSize}px ${family}`;
};

/**
 * Devuelve el texto ya formateado listo para pintar (mayúsculas, trim, limita
 * longitud máxima). Si está vacío, usa el placeholder por defecto.
 */
export const formatStickerText = (rawValue, textConfig) => {
	const fallback = textConfig.defaultText || "";
	const value =
		rawValue == null || String(rawValue).trim() === ""
			? fallback
			: String(rawValue);
	let out = value.slice(0, textConfig.maxChars || 32);
	if (textConfig.uppercase) {
		out = out.toUpperCase();
	} else if (textConfig.capitalize) {
		// Solo la primera letra en mayúscula, el resto en minúscula.
		out = out.charAt(0).toUpperCase() + out.slice(1).toLowerCase();
	}
	return out;
};

/**
 * Devuelve el rectángulo (en px) del área de texto dentro del sticker.
 * `xPercent`/`yPercent` definen el CENTRO del área.
 */
export const getTextAreaRect = (stickerWidth, stickerHeight, textConfig) => {
	const a = textConfig.area || {};
	const width = (a.widthPercent ?? 0.8) * stickerWidth;
	const height = (a.heightPercent ?? 0.2) * stickerHeight;
	const cx = (a.xPercent ?? 0.5) * stickerWidth;
	const cy = (a.yPercent ?? 0.5) * stickerHeight;
	return {
		x: cx - width / 2,
		y: cy - height / 2,
		width,
		height,
		centerX: cx,
		centerY: cy,
	};
};

/**
 * Dado un array `fontSizeSteps` y la longitud del texto, devuelve el tamaño
 * máximo de fuente correspondiente. Los steps deben estar ordenados por
 * `maxChars` ascendente; se elige el primero cuyo `maxChars` es `>=` length.
 * Si ninguno cumple, se devuelve el `size` del último.
 *
 * Ejemplo:
 *   steps = [{ maxChars: 6, size: 64 }, { maxChars: 12, size: 38 }]
 *   length 4  -> 64
 *   length 9  -> 38
 *   length 20 -> 38
 *
 * Si además existe `textConfig.maxFontSize`, el techo efectivo es
 * `min(paso_elegido, maxFontSize)`: sirve para limitar por arriba, no para
 * subir el tamaño por encima del `size` del paso (para eso hay que subir los
 * números en `fontSizeSteps`).
 */
const resolveMaxFontFromSteps = (steps, length) => {
	if (!Array.isArray(steps) || steps.length === 0) return null;
	for (const step of steps) {
		if (length <= (step.maxChars ?? Infinity)) return step.size;
	}
	return steps[steps.length - 1].size;
};

/**
 * Calcula el tamaño de fuente y la compresión horizontal necesarios para que
 * `text` quepa dentro del rectángulo dado usando la configuración `textConfig`.
 *
 * Estrategia:
 *  1. Determinamos un `maxFontSize` efectivo:
 *     - Si `textConfig.fontSizeSteps` existe, elegimos el tamaño según la
 *       longitud del texto (más explícito y predecible por sticker).
 *     - Si no, usamos `textConfig.maxFontSize`.
 *  2. Partimos de ahí y bajamos de 1 en 1 hasta que entra, sin pasar del
 *     `minFontSize`.
 *  3. Aplicamos un pequeño margen de seguridad en el ancho (~8%) para
 *     absorber imprecisiones de medición de canvas con ciertas fuentes.
 *  4. Si al llegar a `minFontSize` aún sobra ancho, bajamos el px por debajo
 *     del mínimo configurado hasta `absoluteMinFontSize` (4px diseño por defecto),
 *     siempre con scaleX = 1 (evita letras “aplastadas”).
 *  5. Último recurso: compresión horizontal (`scaleX`, suelo `minScaleX` típ. 0.6).
 *
 * Devuelve `{ fontSize, scaleX }`.
 */
export const fitFontSize = (text, rect, textConfig, stickerWidth) => {
	const ctx = getMeasureCtx();
	const minF = textConfig.minFontSize ?? 10;
	const maxFromConfig = textConfig.maxFontSize ?? 48;
	const maxFromSteps = resolveMaxFontFromSteps(
		textConfig.fontSizeSteps,
		text.length,
	);
	let maxF =
		maxFromSteps != null ? maxFromSteps : maxFromConfig;
	if (
		maxFromSteps != null &&
		textConfig.maxFontSize != null &&
		Number.isFinite(textConfig.maxFontSize)
	) {
		maxF = Math.min(maxFromSteps, textConfig.maxFontSize);
	}
	const MIN_SCALE_X = textConfig.minScaleX ?? 0.6;
	const WIDTH_SAFETY = textConfig.widthSafety ?? 0.92;

	// Escala relativa al sticker (baseline 200px de ancho de diseño).
	const scale = Math.max(0.4, stickerWidth / 200);
	/* minF / maxF / fontSizeSteps están en “px de diseño”. El bucle debe bajar de 1 en 1
	   en ese espacio; si se itera en px de pantalla (maxF*scale) y se hace -= 1, con
	   scale < 1 el rango entre 11 y 10 diseño queda < 1 px pantalla y casi no se prueban
	   los tamaños del step (el texto se salta directo a mucho más pequeño). */
	const minDesign = Math.min(minF, maxF);
	const maxDesign = maxF;
	const hardMinDesign = Math.max(2, textConfig.absoluteMinFontSize ?? 4);

	if (!ctx) return { fontSize: maxDesign * scale, scaleX: 1 };

	const letterSpacing = (textConfig.letterSpacing || 0) * scale;
	const padX = rect.width * 0.05;
	const padY = rect.height * 0.1;
	const innerW = rect.width - padX * 2;
	/**
	 * Konva/DOM pintan el stroke fuera del glifo; measureText no lo cuenta.
	 * strokeWidth en config va escalado igual que en layout (baseline 200px).
	 */
	const strokeW = Number(textConfig.strokeWidth) || 0;
	const strokeExtraW =
		strokeW > 0
			? strokeW * scale * (textConfig.strokeFitFactor ?? 2.6)
			: 0;
	const availW =
		Math.max(10, Math.max(0, innerW - strokeExtraW)) * WIDTH_SAFETY;
	const availH = Math.max(10, rect.height - padY * 2);

	const measureWidth = (size) => {
		ctx.font = buildCanvasFont(size, textConfig);
		const metrics = ctx.measureText(text);
		return metrics.width + Math.max(0, text.length - 1) * letterSpacing;
	};

	/* Paso de 0.5 diseño: a scale alto (PDF, 1024px → scale ~5) cada paso
	   entero de diseño salta ~5 px de canvas, y el nombre que cabría a 13.5
	   diseño se pierde entre 14 y 13 ⇒ texto innecesariamente pequeño. */
	const STEP = 0.5;
	for (let d = maxDesign; d >= minDesign; d -= STEP) {
		const fontSize = d * scale;
		const w = measureWidth(fontSize);
		const h = fontSize * 1.15;
		if (w <= availW && h <= availH) {
			return { fontSize, scaleX: 1 };
		}
	}

	// Por debajo de minFontSize (diseño), seguir encogiendo sin scaleX.
	for (let d = minDesign - STEP; d >= hardMinDesign; d -= STEP) {
		const fontSize = d * scale;
		const w = measureWidth(fontSize);
		const h = fontSize * 1.15;
		if (w <= availW && h <= availH) {
			return { fontSize, scaleX: 1 };
		}
	}

	// Último recurso: comprimir horizontalmente en el suelo duro (diseño).
	const fontSizeHard = hardMinDesign * scale;
	const wAtHard = measureWidth(fontSizeHard);
	const scaleX = Math.max(MIN_SCALE_X, availW / Math.max(wAtHard, 1e-6));
	return { fontSize: fontSizeHard, scaleX };
};

/**
 * Ajuste para texto en columna (una letra por línea), típico de etiquetas
 * estrechas / omamori. Limita por el ancho de la columna y por la altura total
 * de la pila de caracteres. No usa scaleX (siempre 1).
 */
export const fitFontSizeVertical = (
	text,
	rect,
	textConfig,
	stickerWidth,
) => {
	const ctx = getMeasureCtx();
	const chars = Array.from(text);
	const n = Math.max(1, chars.length);
	const minF = textConfig.minFontSize ?? 10;
	const maxFromConfig = textConfig.maxFontSize ?? 48;
	const maxFromSteps = resolveMaxFontFromSteps(
		textConfig.fontSizeSteps,
		text.length,
	);
	let maxF =
		maxFromSteps != null ? maxFromSteps : maxFromConfig;
	if (
		maxFromSteps != null &&
		textConfig.maxFontSize != null &&
		Number.isFinite(textConfig.maxFontSize)
	) {
		maxF = Math.min(maxFromSteps, textConfig.maxFontSize);
	}
	const WIDTH_SAFETY = textConfig.widthSafety ?? 0.92;
	const HEIGHT_SAFETY = textConfig.verticalHeightSafety ?? 0.92;
	const lineHeight = textConfig.verticalLineHeight ?? 1.12;

	const scale = Math.max(0.4, stickerWidth / 200);
	const scaledMin = Math.min(minF, maxF) * scale;
	const scaledMax = maxF * scale;

	if (!ctx) return { fontSize: scaledMax, scaleX: 1 };

	const padX = rect.width * 0.06;
	const padY = rect.height * 0.06;
	const availW =
		Math.max(8, rect.width - padX * 2) * WIDTH_SAFETY;
	const availH =
		Math.max(8, rect.height - padY * 2) * HEIGHT_SAFETY;

	const measureColumn = (size) => {
		ctx.font = buildCanvasFont(size, textConfig);
		let maxW = 0;
		for (const ch of chars) {
			const w = ctx.measureText(ch).width;
			if (w > maxW) maxW = w;
		}
		const lh = size * lineHeight;
		const totalH = n * lh;
		return { w: maxW, h: totalH };
	};

	const fits = (size) => {
		const { w, h } = measureColumn(size);
		return w <= availW && h <= availH;
	};

	const ABS_MIN = 3;
	let fontSize = scaledMax;
	while (fontSize > scaledMin) {
		if (fits(fontSize)) {
			return { fontSize, scaleX: 1 };
		}
		fontSize -= 1;
	}
	// Por debajo del mínimo de diseño hasta que quepa (nombres largos).
	while (fontSize >= ABS_MIN) {
		if (fits(fontSize)) {
			return { fontSize, scaleX: 1 };
		}
		fontSize -= 1;
	}
	return { fontSize: ABS_MIN, scaleX: 1 };
};

/**
 * Texto en una sola línea horizontal, luego rotado 90° en pantalla: la “altura”
 * del sticker (ranura estrecha) limita el grosor de la línea; el “ancho” del
 * sticker limita la longitud del string medido horizontalmente antes de girar.
 * Tras rotar 90° CW: ocupación horizontal ≈ alto de línea, ocupación vertical ≈ ancho del texto.
 */
export const fitFontSizeVerticalRotated = (
	text,
	rect,
	textConfig,
	stickerWidth,
) => {
	const ctx = getMeasureCtx();
	const minF = textConfig.minFontSize ?? 10;
	const maxFromConfig = textConfig.maxFontSize ?? 48;
	const maxFromSteps = resolveMaxFontFromSteps(
		textConfig.fontSizeSteps,
		text.length,
	);
	let maxF =
		maxFromSteps != null ? maxFromSteps : maxFromConfig;
	if (
		maxFromSteps != null &&
		textConfig.maxFontSize != null &&
		Number.isFinite(textConfig.maxFontSize)
	) {
		maxF = Math.min(maxFromSteps, textConfig.maxFontSize);
	}
	const WIDTH_SAFETY = textConfig.widthSafety ?? 0.92;
	const HEIGHT_SAFETY = textConfig.verticalHeightSafety ?? 0.92;
	const MIN_SCALE_X = textConfig.minScaleX ?? 0.6;
	/** Alto de la caja de línea sin rotar (encaja en la ranura horizontal tras girar). */
	const lineBoxMult = textConfig.verticalLineHeight ?? 1.15;

	const scale = Math.max(0.4, stickerWidth / 200);
	const scaledMin = Math.min(minF, maxF) * scale;
	const scaledMax = maxF * scale;

	if (!ctx) return { fontSize: scaledMax, scaleX: 1 };

	const letterSpacing = (textConfig.letterSpacing || 0) * scale;
	/* Menos padding que columna vertical: la etiqueta ya es la “caja” visual. */
	const padFrac = textConfig.verticalRotateInnerPad ?? 0.022;
	const padX = rect.width * padFrac;
	const padY = rect.height * padFrac;
	const availW =
		Math.max(8, rect.width - padX * 2) * WIDTH_SAFETY;
	const availH =
		Math.max(8, rect.height - padY * 2) * HEIGHT_SAFETY;

	const measureLineWidth = (size) => {
		ctx.font = buildCanvasFont(size, textConfig);
		const base = ctx.measureText(text).width;
		return base + Math.max(0, text.length - 1) * letterSpacing;
	};

	/** Tras rotar 90° CW: ancho pantalla ≈ alto de línea; alto pantalla ≈ ancho texto × scaleX. */
	const fits = (size, sx = 1) => {
		const lineW = measureLineWidth(size) * sx;
		const lineH = size * lineBoxMult;
		return lineH <= availW && lineW <= availH;
	};

	const ABS_MIN = 3;
	let fontSize = scaledMax;
	while (fontSize > scaledMin) {
		if (fits(fontSize, 1)) {
			return { fontSize, scaleX: 1 };
		}
		fontSize -= 1;
	}
	while (fontSize >= ABS_MIN) {
		if (fits(fontSize, 1)) {
			return { fontSize, scaleX: 1 };
		}
		const w = measureLineWidth(fontSize);
		const sxNeeded = availH / Math.max(w, 1e-6);
		if (
			sxNeeded >= MIN_SCALE_X &&
			sxNeeded <= 1 &&
			fits(fontSize, sxNeeded)
		) {
			return { fontSize, scaleX: sxNeeded };
		}
		fontSize -= 1;
	}
	const sx = Math.max(
		MIN_SCALE_X,
		availH / Math.max(measureLineWidth(ABS_MIN), 1e-6),
	);
	return { fontSize: ABS_MIN, scaleX: Math.min(1, sx) };
};

/**
 * Devuelve props listas para pasar a un `<text>` SVG o a un `KonvaText` o a un
 * elemento DOM, a partir de la config del sticker, el texto del usuario y el
 * tamaño físico del sticker.
 */
export const computeStickerTextLayout = (
	rawValue,
	textConfig,
	stickerWidth,
	stickerHeight,
) => {
	const text = formatStickerText(rawValue, textConfig);
	const rect = getTextAreaRect(stickerWidth, stickerHeight, textConfig);
	const curveBuilt = buildCurve(textConfig.curve, rect);

	const isVerticalRotate = Boolean(textConfig.verticalRotate);
	const isVertical =
		Boolean(textConfig.vertical) || isVerticalRotate;
	const { fontSize, scaleX } = isVerticalRotate
		? fitFontSizeVerticalRotated(text, rect, textConfig, stickerWidth)
		: isVertical
			? fitFontSizeVertical(text, rect, textConfig, stickerWidth)
			: curveBuilt
				? fitFontSizeCurved(
						text,
						rect,
						textConfig,
						stickerWidth,
						curveBuilt,
					)
				: fitFontSize(text, rect, textConfig, stickerWidth);
	const scale = Math.max(0.4, stickerWidth / 200);
	const strokeWidth = (textConfig.strokeWidth || 0) * scale;
	const letterSpacing = (textConfig.letterSpacing || 0) * scale;

	let shadow = null;
	let textShadowCSS = null;

	if (
		Array.isArray(textConfig.shadowLayers) &&
		textConfig.shadowLayers.length > 0
	) {
		const shadowLayersScaled = textConfig.shadowLayers.map((s) => ({
			color: s.color || "rgba(0,0,0,0.25)",
			offsetX: (s.offsetX ?? 0) * scale,
			offsetY: (s.offsetY ?? 0) * scale,
			blur: (s.blur ?? 0) * scale,
		}));
		textShadowCSS = shadowLayersScaled
			.map(
				(s) =>
					`${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.color}`,
			)
			.join(", ");
		// Konva/canvas solo admiten una sombra: la que más “profundidad” aporta.
		shadow = shadowLayersScaled.reduce((best, cur) => {
			const score = cur.offsetY + cur.blur * 0.2;
			const bestScore = best.offsetY + best.blur * 0.2;
			return score > bestScore ? cur : best;
		}, shadowLayersScaled[0]);
	} else if (textConfig.shadow) {
		shadow = {
			color: textConfig.shadow.color || "rgba(0,0,0,0.25)",
			offsetX: (textConfig.shadow.offsetX || 0) * scale,
			offsetY: (textConfig.shadow.offsetY || 0) * scale,
			blur: (textConfig.shadow.blur || 0) * scale,
		};
	}

	const fontWeight = textConfig.fontWeight || 400;
	const isItalic = textConfig.fontStyle === "italic";
	const isBold = fontWeight >= 700;
	// Konva acepta un único string combinado para `fontStyle`.
	const konvaFontStyle =
		isBold && isItalic
			? "bold italic"
			: isItalic
				? "italic"
				: isBold
					? "bold"
					: "normal";

	// Path de la curva (reusa el objeto ya construido arriba si existía).
	const curve = curveBuilt;

	const verticalLineHeight = textConfig.verticalLineHeight ?? 1.12;

	const verticalRotateInnerPadFrac = isVerticalRotate
		? textConfig.verticalRotateInnerPad ?? 0.022
		: undefined;

	return {
		text,
		rect,
		fontSize,
		scaleX,
		vertical: isVertical,
		verticalRotate: isVerticalRotate,
		verticalRotateInnerPadFrac,
		verticalLineHeight,
		strokeWidth,
		letterSpacing,
		shadow,
		textShadowCSS,
		curve,
		align: textConfig.align || "center",
		color: textConfig.color || "#ffffff",
		stroke: textConfig.stroke || null,
		fontFamily: textConfig.fontFamily || "sans-serif",
		fontWeight,
		fontStyle: isItalic ? "italic" : "normal",
		konvaFontStyle,
	};
};

// ---------------------------------------------------------------------------
// Texto curvado (opcional)
// ---------------------------------------------------------------------------

/**
 * Construye los datos de una curva a partir de la config `textConfig.curve`.
 *
 * Config soportada:
 *   curve: {
 *     type: "arc",
 *     // Altura del arco, como fracción del alto del rect.
 *     //   0        => texto recto
 *     //   > 0      => arco cóncavo hacia arriba (sonrisa, peak abajo)
 *     //   < 0      => arco cóncavo hacia abajo (ceja, peak arriba)
 *     archHeight: -0.2,
 *   }
 *
 * Devuelve `null` si no hay curva.
 *
 * El arco se implementa con una curva cuadrática Bezier cuyos extremos están
 * en el eje horizontal central del rect; el punto de control se desplaza
 * verticalmente `2 * archHeight * rect.height` (para que el "peak" visible
 * acabe a `archHeight` del eje, que es la semántica intuitiva para el
 * diseñador).
 *
 * Devolvemos ambos: path en coordenadas absolutas del sticker (para Konva y
 * el canvas del PDF) y path relativo al rect (para el SVG de la preview DOM).
 */
export const buildCurve = (curveConfig, rect) => {
	if (!curveConfig) return null;
	const archHeight = curveConfig.archHeight ?? 0;
	if (archHeight === 0) return null;

	const halfY = rect.height / 2;
	const peakOffset = 2 * archHeight * rect.height;

	// Absoluto (dentro del sticker)
	const abs = {
		p0: { x: rect.x, y: rect.y + halfY },
		p1: { x: rect.x + rect.width, y: rect.y + halfY },
		cp: {
			x: rect.x + rect.width / 2,
			y: rect.y + halfY + peakOffset,
		},
	};
	const pathAbs = `M${abs.p0.x},${abs.p0.y} Q${abs.cp.x},${abs.cp.y} ${abs.p1.x},${abs.p1.y}`;

	// Local (origen en el rect) — útil para el SVG de la preview DOM.
	const loc = {
		p0: { x: 0, y: halfY },
		p1: { x: rect.width, y: halfY },
		cp: { x: rect.width / 2, y: halfY + peakOffset },
	};
	const pathLocal = `M${loc.p0.x},${loc.p0.y} Q${loc.cp.x},${loc.cp.y} ${loc.p1.x},${loc.p1.y}`;

	return {
		type: curveConfig.type || "arc",
		archHeight,
		pathAbs,
		pathLocal,
		p0: abs.p0,
		p1: abs.p1,
		cp: abs.cp,
	};
};

/**
 * Punto de una curva cuadrática Bezier en el parámetro t ∈ [0,1].
 */
export const bezierPointAt = (p0, cp, p1, t) => {
	const u = 1 - t;
	return {
		x: u * u * p0.x + 2 * u * t * cp.x + t * t * p1.x,
		y: u * u * p0.y + 2 * u * t * cp.y + t * t * p1.y,
	};
};

/**
 * Tangente (ángulo en radianes) de una curva cuadrática Bezier en t.
 */
export const bezierTangentAngleAt = (p0, cp, p1, t) => {
	const u = 1 - t;
	const dx = 2 * u * (cp.x - p0.x) + 2 * t * (p1.x - cp.x);
	const dy = 2 * u * (cp.y - p0.y) + 2 * t * (p1.y - cp.y);
	return Math.atan2(dy, dx);
};

/**
 * Longitud aproximada de una curva cuadrática Bezier y tabla de arc-length
 * (para convertir distancia a parámetro t).
 */
export const sampleBezierArcLength = (p0, cp, p1, steps = 100) => {
	const lengths = [0];
	let prev = bezierPointAt(p0, cp, p1, 0);
	for (let i = 1; i <= steps; i += 1) {
		const t = i / steps;
		const pt = bezierPointAt(p0, cp, p1, t);
		lengths.push(lengths[i - 1] + Math.hypot(pt.x - prev.x, pt.y - prev.y));
		prev = pt;
	}
	return { lengths, total: lengths[steps], steps };
};

/**
 * Texto sobre Bezier: el reparto usa la longitud de arco (`sampleBezierArcLength`).
 * Si medimos solo el ancho del rect (cuerda), nombres largos pasan el filtro y se
 * salen del path ⇒ recorte en SVG/viewBox y en Konva/canvas (centro negativo).
 */
export const fitFontSizeCurved = (
	text,
	rect,
	textConfig,
	stickerWidth,
	curve,
) => {
	const ctx = getMeasureCtx();
	const minF = textConfig.minFontSize ?? 10;
	const maxFromConfig = textConfig.maxFontSize ?? 48;
	const maxFromSteps = resolveMaxFontFromSteps(
		textConfig.fontSizeSteps,
		text.length,
	);
	let maxF =
		maxFromSteps != null ? maxFromSteps : maxFromConfig;
	if (
		maxFromSteps != null &&
		textConfig.maxFontSize != null &&
		Number.isFinite(textConfig.maxFontSize)
	) {
		maxF = Math.min(maxFromSteps, textConfig.maxFontSize);
	}
	const WIDTH_SAFETY = textConfig.widthSafety ?? 0.92;
	const MIN_SCALE_X = textConfig.minScaleX ?? 0.6;

	const scale = Math.max(0.4, stickerWidth / 200);
	const minDesign = Math.min(minF, maxF);
	const maxDesign = maxF;
	const hardMinDesign = Math.max(2, textConfig.absoluteMinFontSize ?? 4);

	const letterSpacing = (textConfig.letterSpacing || 0) * scale;
	const padY = rect.height * 0.1;
	const availH = Math.max(10, rect.height - padY * 2);

	const arcData = sampleBezierArcLength(curve.p0, curve.cp, curve.p1, 120);
	const endsMargin =
		textConfig.curveEndsMarginRatio != null
			? textConfig.curveEndsMarginRatio
			: 0.03;
	let availLen = Math.max(
		8,
		arcData.total * WIDTH_SAFETY * (1 - 2 * endsMargin),
	);
	const strokeW = Number(textConfig.strokeWidth) || 0;
	if (strokeW > 0) {
		availLen = Math.max(
			6,
			availLen -
				strokeW * scale * (textConfig.strokeFitFactor ?? 2.6),
		);
	}

	const measureAlong = (size) => {
		if (!ctx) return 0;
		ctx.font = buildCanvasFont(size, textConfig);
		if (!letterSpacing) {
			return ctx.measureText(text).width;
		}
		let w = 0;
		for (const ch of Array.from(text)) {
			w += ctx.measureText(ch).width;
		}
		w += Math.max(0, text.length - 1) * letterSpacing;
		return w;
	};

	const fits = (size) =>
		measureAlong(size) <= availLen && size * 1.15 <= availH;

	if (!ctx) return { fontSize: maxDesign * scale, scaleX: 1 };

	const STEP = 0.5;
	for (let d = maxDesign; d >= minDesign; d -= STEP) {
		const fontSize = d * scale;
		if (fits(fontSize)) {
			return { fontSize, scaleX: 1 };
		}
	}

	for (let d = minDesign - STEP; d >= hardMinDesign; d -= STEP) {
		const fontSize = d * scale;
		if (fits(fontSize)) {
			return { fontSize, scaleX: 1 };
		}
	}

	const fontSizeHard = hardMinDesign * scale;
	const wAtHard = measureAlong(fontSizeHard);
	const sx = Math.max(MIN_SCALE_X, availLen / Math.max(wAtHard, 1e-6));
	return { fontSize: fontSizeHard, scaleX: sx };
};

/**
 * Dada una tabla de arc-length, devuelve el `t` correspondiente a una
 * distancia `d` a lo largo de la curva.
 */
export const tFromArcLength = (arc, d) => {
	const { lengths, steps } = arc;
	if (d <= 0) return 0;
	if (d >= arc.total) return 1;
	let lo = 0;
	let hi = steps;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if (lengths[mid] < d) lo = mid + 1;
		else hi = mid;
	}
	const idx = Math.max(1, Math.min(steps, lo));
	const prev = lengths[idx - 1];
	const next = lengths[idx];
	const ratio = next > prev ? (d - prev) / (next - prev) : 0;
	return (idx - 1 + ratio) / steps;
};
